import { Plus } from "lucide-react";
import React, { memo, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { IgIconButton, IgTooltip } from "@intelligems/ig-theme";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import { GraphConfig, GraphType } from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { InsufficientCogsAlert } from "@src/features/analytics/baseDashboard/InsufficientCogsAlert";
import { NoCogsGraphPlaceholder } from "@src/features/analytics/baseDashboard/NoCogsGraphPlaceholder";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import {
  shouldShowInsufficientCogsWarning,
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useSelector } from "@src/store";
import {
  selectedExperience,
  setExperienceTab,
} from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

interface KeyMetricsAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const KeyMetricsAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: KeyMetricsAnalyticsPageProps) => {
    const { isReadOnly } = useReadOnlyMode();
    const dispatch = useDispatch();
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const shouldShowProfit = useMemo(() => {
      return shouldShowProfitData(data[0]?.data?.metrics || []);
    }, [data]);
    const onMetricDetailsClick = () => {};
    const isAnalyticsFetching = useAnalyticsFetching();
    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        {shouldShowInsufficientCogsWarning(data) && (
          <Grid>
            <InsufficientCogsAlert />
          </Grid>
        )}
        <Grid>
          <BaseMetricSection
            groupTitle={"Key Metrics"}
            showCompareGroups={true}
            rightSideButtons={
              isReadOnly
                ? []
                : [
                    <IgTooltip
                      title={"Add Key Metrics"}
                      key={"add-key-metrics-tooltip"}
                    >
                      <IgIconButton
                        key={"add-key-metrics"}
                        sx={{ padding: "4px" }}
                        onClick={() =>
                          dispatch(
                            setExperienceTab(TabEnumFe.CONFIGURE_ANALYTICS)
                          )
                        }
                      >
                        <Plus size={24} />
                      </IgIconButton>
                    </IgTooltip>,
                  ]
            }
          >
            {graphConfigs.map((config, index) => {
              return (
                <Grid key={index} size={gridWidth}>
                  {shouldShowProfitPlaceholder(shouldShowProfit, config) ? (
                    <NoCogsGraphPlaceholder config={config} />
                  ) : (
                    <BaseGraphPaper>
                      <GraphsWrapper
                        isDataLoading={isAnalyticsFetching > 0}
                        totalTestVisitors={totalExperienceVisitors}
                        config={config}
                        onMetricDetailsClick={onMetricDetailsClick}
                        testDuration={testDuration}
                        renderLocation={"experiment"}
                        data={selectData({
                          dashboardData: data,
                          config,
                          _resource: experience,
                          locale: storeCurrencyConfig?.value?.locale,
                          metricFilterIndex: 0,
                          variations,
                        })}
                        rawData={data}
                        compareVariationId={compareVariationId}
                      />
                    </BaseGraphPaper>
                  )}
                </Grid>
              );
            })}
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={[
              ...graphConfigs,
              {
                title: ["Visitors"],
                metrics: ["n_visitors"],
                datasetId: "variation_overview",
                dataTypes: ["number"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
              {
                title: ["Orders"],
                metrics: ["n_orders"],
                datasetId: "variation_overview",
                dataTypes: ["number"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
              {
                title: ["Net Revenue"],
                metrics: ["net_revenue"],
                datasetId: "variation_overview",
                dataTypes: ["currency"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
            ]}
            dropdownOptions={[
              ...graphConfigs,
              {
                title: ["Visitors"],
                metrics: ["n_visitors"],
                datasetId: "variation_overview",
                dataTypes: ["number"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
              {
                title: ["Orders"],
                metrics: ["n_orders"],
                datasetId: "variation_overview",
                dataTypes: ["number"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
              {
                title: ["Net Revenue"],
                metrics: ["net_revenue"],
                datasetId: "variation_overview",
                dataTypes: ["currency"],
                graphType: GraphType.Table,
                hasValueCI: false,
                hasProbability: false,
                hasTimeseries: true,
                hasAudience: false,
                canBeKeyMetric: false,
                impactMetric: undefined,
              },
            ].map((config, index) => ({
              title: config.title[0],
              id: config.impactMetric ? config.impactMetric : config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs}
            testDuration={testDuration}
          />
        </Grid>
      </Stack>
    );
  }
);
